import { AskMentionInput } from "../components/AskMentionInput";
import { AskMatchingRecords } from "../components/ask/AskMatchingRecords";
/**
 * Ask Led — the Ask tab, and ONLY the Ask tab (spec §3.3).
 *
 * ## What is not here, and why
 *
 * The source design gives this screen three tabs: **Ask**, **Automate** and
 * **Create**. Two are cut.
 *
 * **Automate** offers standing rules — "nudge automatically when an item sits
 * 3 days", "tell me the moment a walk produces a high-severity item". Those
 * need a trigger engine: something that watches for a condition and fires on
 * its own, with a schedule, a delivery channel, and a record of what it did.
 * None of that exists. A rules tab that accepted the sentence and then never
 * fired would be the most damaging possible version of this console's one
 * failure mode — the user would stop watching an item BECAUSE they believed
 * something else was.
 *
 * **Create** drafts a task, an owner update, a follow-up. Drafting is a write
 * path, and it is the one part of the design that assumes an assignment can
 * be made from the console. It is cut with Automate.
 *
 * The cuts are structural, not cosmetic: `src/server/index.ts` exposes
 * `/api/ask` and no automate or create route, and there is no tab strip here
 * to hide.
 *
 * ## Where the answer comes from
 *
 * `POST /api/ask` forwards to `backend/`'s existing `POST /api/search/ask-led`
 * with the caller's own credentials. The console does not answer questions
 * itself and holds no model — asking it to would put an LLM in the read
 * service and a second copy of the retrieval logic beside `backend`'s.
 *
 * If this deployment has no backend configured, the composer is disabled and
 * says so. It never accepts a question it cannot answer.
 *
 * ## Recent chats, and why history is the SERVER's
 *
 * The list is a RAIL down the left, not a panel over the conversation
 * (design: "Led AI Console"; user, 2026-09-11). See `AskHistoryRail` for why
 * that is a different thing rather than the same thing moved.
 *
 * `backend` persists every answer it composes (`ask-led-history.model.ts`,
 * grouped into recent conversations) and serves them at
 * `GET /api/search/ask-led/history`; the console reads that through
 * `GET /api/ask/history` and keeps no store of its own. The phone's Ask has
 * the same saved answers. Opening a chat restores its full saved thread.
 *
 * That single store is also what makes the Ask bubble's "Continue in Ask Led"
 * carry a CONVERSATION rather than a string. The bubble's answer was already
 * written down when the reader pressed Continue, so this tab looks the
 * question up and puts the exchange — answer, citation cards and all — back on
 * screen without asking the model a second time. It survives a reload for the
 * same reason: there is no prop to lose, only a record to re-read.
 */

import { useMediaQuery } from "../shell/useMediaQuery";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ApiError, askClientTimeZone, askTimeHorizon, fetchAskHistory, fetchAskSuggestions, postMutation, type AskAnswer, type AskHistoryEntry, type AskHistory } from "../api";
import { ResourceCacheContext, useResource, useResourceCache } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { isMutatingSuggestion } from "../../../../backend/src/shared/askChatPolicy";
import { C } from "../tokens";
import { askAnswerStats } from "../lib/askAnswerStats";
import { useAskAutoScroll } from "../lib/useAskAutoScroll";
import { DictationButton } from "../components/DictationButton";
import { ChatSendIcon } from "../components/ChatInputChrome";
import {
  ASK_RAIL_COLLAPSED_WIDTH,
  ASK_RAIL_WIDTH,
  AskHistoryRail,
} from "../components/AskHistoryRail";
import {
  ConsoleViewProps,
  META,
} from "../components/chrome";
import {
  AnswerBody,
  ANSWER_FONT_SIZE,
  ANSWER_LINE_HEIGHT,
} from "../components/ask/AnswerBody";
import { Lockup } from "../components/Lockup";
import { copyToClipboard } from "../lib/clipboard";
import { takeAskAttachment } from "../lib/askHandoff";
import { SourcesCard } from "../components/ask/SourcesCard";
import { citedInOrder } from "../components/ask/askCitations";
import type { CitationTarget } from "../components/ask/citationTarget";

/**
 * The starter questions.
 *
 * All ASK-shaped — every one of them is answerable from records that already
 * exist. The design's Automate and Create prompts are deliberately absent
 * rather than shown greyed out: a disabled suggestion still teaches the user
 * that the feature is there.
 */
export const ASK_SUGGESTIONS: Array<{ q: string; note?: string }> = [
  { q: "What happened on my most recent walk?" },
  { q: "What tasks are still open this week?" },
  { q: "Summarize this week's site activity." },
  { q: "Any safety issues flagged recently?" },
];

/**
 * The question the Ask bubble handed over, if any.
 *
 * `?ask=` is set by "Continue in Ask Led". It never re-asks: the bubble
 * already spent an LLM call on it, and firing a second one on arrival would
 * bill the same question twice and could return a different answer than the
 * one the reader clicked Continue on.
 *
 * What it does instead is look the question up in the caller's SERVER-SIDE
 * history — `backend` persisted the answer the moment the bubble got it — and
 * open that exchange. That is what makes Continue carry the conversation
 * rather than a string, and it is why a reload of the same URL rebuilds the
 * same screen: there is no prop to lose. When no record matches (history
 * unreachable, or the entry is outside the recent-chat list) the
 * question falls back to PRE-FILLING the composer, so the reader still sees
 * what they asked and can press Ask.
 */
export function handedOverQuestion(search: string): string {
  try {
    return new URLSearchParams(search).get("ask")?.trim() ?? "";
  } catch {
    return "";
  }
}

/** Home's search button is a submit, while the other `?ask=` handoffs may
 * point at an already-persisted exchange. Keeping that intent explicit stops
 * a pasted history link from spending a second Ask request. */
function submitsHandedOverQuestion(search: string): boolean {
  try {
    return new URLSearchParams(search).get("askSubmit") === "true";
  } catch {
    return false;
  }
}

/** Consume Home's one-shot submit intent before the network request begins.
 * If the page reloads while Ask Led is thinking, it must not ask twice. */
function consumeAskSubmitIntent(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("askSubmit")) return;
  url.searchParams.delete("askSubmit");
  url.searchParams.delete("askChat");
  window.history.replaceState(null, "", url.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Browser-back from a cited record must land on the exchange, not an empty
 * composer. The view already re-opens `?ask=` from `backend`'s server-side
 * history on mount (`handedOverQuestion`), so putting the question in the URL
 * is the whole mechanism — there is no state to serialize. `replaceState`,
 * not `pushState`: opening an exchange — by asking, or by picking one from
 * Recent asks — is not a navigation, and a push per exchange would make Back
 * walk the reader through their own browsing of the composer or the history
 * panel before it ever left the page.
 *
 * ## Why this also dispatches a `popstate`
 *
 * `Shell.tsx` holds the console's query string as its OWN React state
 * (`search`), updated only inside its `navigate()` and by a `popstate`
 * listener on the real Back/Forward buttons — never by reading
 * `window.location` on every render. Writing straight to `window.history`
 * here (asking a question, or opening one from Recent asks, is not a
 * navigation the shell drives) moved the address bar without moving the
 * shell: `hrefForItem`/`hrefForWalk` are BUILT from the shell's `search`, so
 * right after asking, a citation's own `href` — and a reload via
 * `ItemDetail`'s back control — carried a URL WITHOUT `?ask=` even though
 * `window.location.search` already had it. The browser's Back button worked;
 * the shell's own links did not.
 *
 * Dispatching a synthetic `popstate` after `replaceState` is the smallest
 * fix: it is exactly the event `Shell.tsx` already listens for, so the shell
 * re-reads `location.search` into its own state the same way a real Back
 * press does. That re-render also re-runs THIS view's `search`-keyed
 * effects — every call site below sets `openedHandoff.current` first so that
 * re-entry finds the handoff already handled and fetches nothing twice, and
 * `ask()` never re-issues its `POST /api/ask` from this — see the guards at
 * each call site.
 */
function syncAskUrl(query: string | null, conversationId?: string | null): void {
  const url = new URL(window.location.href);
  if (query) url.searchParams.set("ask", query);
  else url.searchParams.delete("ask");
  if (conversationId) url.searchParams.set("askChat", conversationId);
  else url.searchParams.delete("askChat");
  window.history.replaceState(null, "", url.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** The reading measure is wider than the original 720px artboard, while
 * still short enough for prose. */
const ASK_MEASURE = 800;

/** A completed turn displayed above the newest exchange in the saved chat. */
type PriorAskTurn = {
  id: string;
  query: string;
  answer: AskAnswer | null;
  problem: string | null;
};

/** The settled turns in the active conversation. They deliberately use the
 * same bubbles, answer body and source cards as the newest turn below; a
 * follow-up should read like one continuous exchange, not a list of previews.
 */
function PriorAskTurnView({
  search,
  turn,
  onOpen,
  hrefFor,
}: {
  search: string;
  turn: PriorAskTurn;
  onOpen: (target: CitationTarget) => void;
  hrefFor: (target: CitationTarget) => string;
}) {
  return (
    <div data-testid="ask-thread-turn" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        data-testid="ask-question"
        className="ask-sent"
        style={{
          alignSelf: "flex-end",
          maxWidth: "72%",
          padding: "12px 16px",
          borderRadius: "18px 18px 6px 18px",
          background: C.askSaid,
          color: C.ink,
          fontSize: 17.25,
          lineHeight: 1.5,
        }}
      >
        {turn.query}
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: C.ink,
            opacity: 0.9,
            height: ANSWER_FONT_SIZE * ANSWER_LINE_HEIGHT,
          }}
        >
          <Lockup variant="mark" height={22} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {turn.problem && (
            <div
              data-testid="ask-error"
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                background: C.redSoft,
                fontSize: 15.52,
                lineHeight: 1.5,
              }}
            >
              {turn.problem}
            </div>
          )}
          {turn.answer && (
            <>
              <AnswerBody
                text={turn.answer.answer}
                references={turn.answer.references}
                onOpen={onOpen}
                hrefFor={hrefFor}
              />
              <SourcesCard
                text={turn.answer.answer}
                references={turn.answer.references}
                onOpen={onOpen}
                hrefFor={hrefFor}
              />
              <AskMatchingRecords resultSets={turn.answer.resultSets} search={search} onOpen={onOpen} hrefFor={hrefFor} />
              <AnswerDetails answer={turn.answer} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AnswerDetails({ answer }: { answer: AskAnswer }) {
  return <details data-testid="ask-answer-details" style={{ fontSize: 13, lineHeight: 1.5, color: C.sub2 }}>
    <summary className="kb" style={{ cursor: "pointer" }}>Answer details</summary>
    <div style={{ marginTop: 6 }}>{askAnswerStats(answer)}</div>
  </details>;
}

export function AskLed(props: ConsoleViewProps) {
  const cache = useResourceCache();
  const purge = useSyncExternalStore(cache.subscribe, cache.purgeVersion, cache.purgeVersion);
  const scope = resourceQuery(props.search, ["workspace", "site"]);
  return <ResourceCacheContext.Provider value={cache}>
    <AskLedSession key={`${scope}:${purge}`} {...props} />
  </ResourceCacheContext.Provider>;
}

function AskLedSession({
  search,
  refreshKey,
  onOpenItem,
  onOpenWalk,
  hrefForItem,
  hrefForWalk,
}: ConsoleViewProps) {
  const cache = useResourceCache();
  // Initialised from the URL ONCE, on mount. Not synced to `search` on every
  // render: the user types in this box, and re-reading the URL under them
  // would overwrite what they wrote the next time anything else re-rendered.
  const [question, setQuestion] = useState(() => handedOverQuestion(search));
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<AskAnswer | null>(null);
  const [asked, setAsked] = useState<string | null>(null);
  const { scrollRef, threadRef, composerRef, onScroll, followLatest } = useAskAutoScroll(!!asked);
  const [problem, setProblem] = useState<string | null>(null);
  const [priorTurns, setPriorTurns] = useState<PriorAskTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(() =>
    new URLSearchParams(search).has("askChat") && !submitsHandedOverQuestion(search),
  );
  const generation = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => () => { generation.current++; }, []);
  /** Null until a call has been made — the composer is not disabled on a
   *  guess. `postMutation` reports `configured` on every response. */
  const [backendMissing, setBackendMissing] = useState(false);
  const [suggestions, setSuggestions] = useState(ASK_SUGGESTIONS);
  /** Whether the last Copy reached the clipboard. Cleared when the exchange
   *  changes, so a tick never sits over an answer it did not confirm. */
  const [copied, setCopied] = useState<"idle" | "done" | "failed">("idle");

  // ---- Recent chats (the rail down the left — see `AskHistoryRail`)
  const [history, setHistory] = useState<AskHistoryEntry[]>([]);
  /** The failure, when the list could not be read. Kept apart from an empty
   *  list: "you have never asked anything" and "we could not look" must not
   *  read the same. */
  const [historyProblem, setHistoryProblem] = useState<string | null>(null);
  /** The rail is OPEN by default (design): it is context read alongside the
   *  answer, not a panel you open and dismiss. */
  const [historyOpen, setHistoryOpen] = useState(true);
  // On a narrow screen history overlays the answer instead of squeezing it.
  const compact = useMediaQuery("(max-width: 1100px)");
  const [compactHistoryOpen, setCompactHistoryOpen] = useState(false);
  const railOpen = compact ? compactHistoryOpen : historyOpen;
  /** Which past exchange is on screen, so the rail can mark it. `null` once
   *  the reader asks something new — that answer is not in the list yet. */
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  /** One clock for every day bucket in the rail, so two rows cannot land on
   *  different sides of midnight within a single render. */
  const railNow = useMemo(() => new Date(), [history]);
  /** The handed-over question is opened from history AT MOST ONCE. Without
   *  this, every later refresh would yank the reader back to the exchange
   *  they arrived on, discarding whatever they had asked since. */
  const openedHandoff = useRef(false);
  /** Assigned to the real submitter below. The history read runs before that
   * declaration in source, but effects run after the render completes. */
  const askRef = useRef<(query: string) => void>(() => {});

  // A stale conversation URL can return 409 even when this workspace has
  // valid recent chats. Read the rail independently of conversation detail.
  const historyQuery = resourceQuery(search, ["workspace", "site"]);
  const historyRead = useResource<AskHistory>(`ask-history:${historyQuery}`,
    () => fetchAskHistory(historyQuery, true), refreshKey);
  const historyLoading = historyRead.loading && !historyRead.data;
  const [detailId, setDetailId] = useState<string | null>(() =>
    submitsHandedOverQuestion(search) ? null : new URLSearchParams(search).get("askChat"),
  );
  const detailParams = new URLSearchParams(resourceQuery(search, ["workspace", "site"]));
  if (detailId) detailParams.set("askChat", detailId);
  const detailQuery = resourceQuery(`?${detailParams}`, ["workspace", "site", "askChat"]);
  const detailRead = useResource<AskHistory>(detailId ? `ask-history:${detailQuery}` : null,
    async () => {
      const loaded = await fetchAskHistory(detailQuery, true);
      if (!loaded.conversation) throw new ApiError(404, "This conversation could not be loaded. Please reopen it or start a new chat.");
      return loaded;
    }, refreshKey);
  const detailFailure = detailRead.failure ?? detailRead.refreshFailure;
  const conversationNotice = detailId && detailFailure?.status === 409 ? detailFailure.message : null;

  /** Put saved turns and their citations back on screen without an AI call. */
  const restoreConversation = (entry: AskHistoryEntry) => {
    followLatest();
    const turns = entry.turns?.length ? entry.turns : [{ id: entry.id, query: entry.answer.query || entry.query, answer: entry.answer, createdAt: entry.createdAt }];
    const latest = turns.at(-1)!;
    setPriorTurns(turns.slice(0, -1).map((turn) => ({ ...turn, problem: null })));
    setAsked(latest.query);
    setAnswer(latest.answer);
    setConversationId(entry.answer.conversationId ?? entry.id);
  };
  const openExchange = (entry: AskHistoryEntry) => {
    ++generation.current;
    inFlight.current = false;
    setAsking(false);
    setCopied("idle");
    restoreConversation(entry);
    setProblem(null);
    setQuestion("");
    // Desktop history stays open for context; the narrow overlay closes
    // so the selected answer is immediately readable.
    setOpenEntryId(entry.id);
    setCompactHistoryOpen(false);
    // Set BEFORE `syncAskUrl`: its `popstate` re-runs the mount effect below
    // synchronously-ish (on the next render), and this ref is what makes
    // that re-entry a no-op rather than a second, redundant `openExchange`
    // call — see `syncAskUrl`'s doc comment.
    openedHandoff.current = true;
    // See `syncAskUrl` — this is the "I opened a past exchange" half of the
    // one mechanism; `ask()` is the "I just asked" half.
    const id = entry.answer.conversationId ?? entry.id;
    syncAskUrl(entry.answer.query || entry.query, id);
    if (/^[a-f0-9]{24}$/i.test(id)) {
      setRestoring(!entry.turns);
      setDetailId(id);
    } else {
      setDetailId(null);
      setRestoring(false);
    }
  };

  useEffect(() => {
    if (!detailId) return;
    const loaded = detailRead.data;
    const failure = detailRead.failure ?? detailRead.refreshFailure;
    setRestoring(detailRead.loading && !loaded);
    if (failure?.status === 409) {
      setProblem(null);
      setAnswer(null); setPriorTurns([]); setAsked(null); setConversationId(null); setOpenEntryId(null);
      return;
    }
    if (loaded?.conversation) {
      if (!openedHandoff.current) openExchange(loaded.conversation);
      else restoreConversation(loaded.conversation);
      setRestoring(false);
      setProblem(failure?.message ?? null);
    }
    else if (loaded && !detailRead.loading) setProblem("This conversation could not be loaded. Please reopen it or start a new chat.");
    if (failure) {
      setProblem(failure.message);
      if (detailRead.failure) { setAnswer(null); setPriorTurns([]); setAsked(null); setConversationId(null); }
    }
  }, [detailId, detailRead.data, detailRead.failure, detailRead.refreshFailure, detailRead.loading]);

  useEffect(() => {
    let cancelled = false;
    const handedOver = handedOverQuestion(search);
    const submitHandoff = !!handedOver && submitsHandedOverQuestion(search);
    // Home explicitly starts a new chat, even for a question asked before.
    // Submit before loading history so a slow read or stale chat ID cannot
    // turn that intent into restoring an old exchange.
    if (!openedHandoff.current && submitHandoff) {
      // Let StrictMode's mount cleanup finish before starting the request;
      // that cleanup invalidates responses belonging to an unmounted view.
      queueMicrotask(() => {
        if (cancelled || openedHandoff.current) return;
        openedHandoff.current = true;
        consumeAskSubmitIntent();
        askRef.current(handedOver);
      });
    }
    const loaded = historyRead.data;
    const failure = historyRead.failure ?? historyRead.refreshFailure;
    setHistoryProblem(failure?.message ?? null);
    if (historyRead.failure) {
      setHistory([]);
      setRestoring(false);
      if ([401, 403, 404].includes(historyRead.failure.status)) {
        setAnswer(null); setPriorTurns([]); setAsked(null); setConversationId(null); setDetailId(null);
      }
    }
    if (loaded) {
      setHistory(loaded.entries);

      const chatId = new URLSearchParams(search).get("askChat");
      // Explicit conversation links restore through detailRead, including
      // conversations outside the recent list. Question-only handoffs can
      // still find their saved exchange in the rail.
      if (openedHandoff.current || submitHandoff || chatId || !handedOver) return () => { cancelled = true; };
      const match = loaded.entries.find((entry) => entry.query.trim() === handedOver || entry.answer.query?.trim() === handedOver);
      if (!match) {
        setRestoring(false);
        return () => { cancelled = true; };
      }
      openedHandoff.current = true;
      void openExchange(match);
    }
    return () => { cancelled = true; };
  }, [search, refreshKey, historyRead.data, historyRead.failure, historyRead.refreshFailure]);

  // Match mobile: use the backend's current, user-specific suggestions when
  // available, but only surface read-only prompts and keep a known-good
  // fallback when that optional read is unavailable.
  useEffect(() => {
    let cancelled = false;
    void fetchAskSuggestions(search).then((loaded) => {
      if (cancelled) return;
      const safe = loaded.filter((prompt) => !isMutatingSuggestion(prompt)).slice(0, 4);
      if (safe.length > 0) setSuggestions(safe.map((q) => ({ q })));
    });
    return () => {
      cancelled = true;
    };
  }, [search, refreshKey]);

  const ask = async (text: string) => {
    const query = text.trim();
    if (!query || inFlight.current || restoring) return;
    followLatest();
    openedHandoff.current = true;
    inFlight.current = true;
    const activeGeneration = ++generation.current;
    const previousTurns = [...priorTurns, ...(asked ? [{ id: `${asked}:${priorTurns.length}`, query: asked, answer, problem }] : [])];

    // A submitted message belongs in the thread, not in the composer. Clear
    // this before the network call so Enter and the send button both feel
    // immediate, even while Led is still reading the walks.
    setQuestion("");
    if (asked) {
      setPriorTurns((current) => [
        ...current,
        {
          id: `${asked}:${current.length}`,
          query: asked,
          answer,
          problem,
        },
      ]);
    }
    setDetailId(null);
    setAsking(true);
    setCopied("idle");
    setProblem(null);
    setAnswer(null);
    setAsked(query);
    setOpenEntryId(null);

    // The file Home was holding, if this ask came from there. Taken ONCE —
    // see `askHandoff` on why it must not survive to the next question.
    const attachment = takeAskAttachment();
    const result = await postMutation<AskAnswer>("/api/ask", search, {
      query,
      ...(conversationId ? { conversationId } : {}),
      timeZone: askClientTimeZone(),
      timeHorizon: askTimeHorizon(search),
      ...(attachment
        ? {
            attachment: {
              mediaType: attachment.mediaType,
              data: attachment.data,
              name: attachment.name,
            },
          }
        : {}),
    });
    if (activeGeneration !== generation.current) return;
    inFlight.current = false;
    setAsking(false);

    if (!result.ok) {
      setBackendMissing(!result.configured);
      setProblem(
        result.configured
          ? (result.error ?? `Ask Led AI could not answer that (${result.status}).`)
          : "Ask Led AI runs on the LedAI backend, and this console is not configured to reach it. Nothing was sent.",
      );
      return;
    }
    setBackendMissing(false);
    const composed = result.result ?? null;
    setAnswer(composed);
    if (composed) {
      const id = composed.conversationId ?? `${composed.generatedAt || "ask"}:${query}`;
      setConversationId(composed.conversationId ?? null);
      setOpenEntryId(id);
      const turns = previousTurns.filter((turn): turn is PriorAskTurn & { answer: AskAnswer } => !!turn.answer).map((turn) => ({ id: turn.id, query: turn.query, answer: turn.answer, createdAt: turn.answer.generatedAt }));
      turns.push({ id: `${id}:${turns.length}`, query, answer: composed, createdAt: composed.generatedAt });
      // `backend` has already persisted this one; showing it in Recent asks
      // now rather than on the next refresh keeps the list honest about what
      // the reader has just done.
      setHistory((current) => [
        {
          // Follow-ups update one stable conversation row in the rail.
          id,
          query: turns[0]?.query ?? query,
          answer: composed,
          createdAt: composed.generatedAt,
          turns,
        },
        ...current.filter((entry) => entry.id !== id),
      ]);
      const entry: AskHistoryEntry = { id, query: turns[0]?.query ?? query, answer: composed, createdAt: composed.generatedAt, turns };
      const entries = [entry, ...history.filter((item) => item.id !== id)];
      const scopeQuery = resourceQuery(search, ["workspace", "site"]);
      cache.set<AskHistory>(`ask-history:${scopeQuery}`, { entries });
      const savedParams = new URLSearchParams(scopeQuery);
      savedParams.set("askChat", id);
      cache.set<AskHistory>(`ask-history:${resourceQuery(`?${savedParams}`, ["workspace", "site", "askChat"])}`, { entries, conversation: entry });
      // Set BEFORE `syncAskUrl`, for the same reason `openExchange` does:
      // its `popstate` re-runs the history effect below, and the server's
      // history response will already contain this exchange (it persists
      // before answering), so without this the effect would find a "match"
      // and re-run `openExchange` on it — redundant, not incorrect, but not
      // the no-op `syncAskUrl`'s doc comment promises either.
      openedHandoff.current = true;
      // See `syncAskUrl` — this is the "I just asked" half of the one
      // mechanism; `openExchange` is the "I opened a past exchange" half.
      syncAskUrl(query, composed.conversationId);
    }
  };
  askRef.current = (query) => {
    void ask(query);
  };

  const reset = () => {
    followLatest();
    openedHandoff.current = true;
    generation.current++;
    inFlight.current = false;
    setAsking(false);
    setRestoring(false);
    setDetailId(null);
    setConversationId(null);
    setCompactHistoryOpen(false);
    setCopied("idle");
    setPriorTurns([]);
    setAnswer(null);
    setAsked(null);
    setProblem(null);
    setQuestion("");
    setOpenEntryId(null);
    // Clears the same `?ask=` the exchange wrote. Without this, "Start over"
    // cleared every piece of REACT state but left the URL saying otherwise —
    // a reload after it resurrected the exact exchange the reader had just
    // dismissed. `handedOverQuestion(search)` will be empty once this lands,
    // so the mount effect's handoff guard never fires on the way back down —
    // no need to touch `openedHandoff` here.
    syncAskUrl(null);
  };

  /** The answer as text, for pasting into an email or Procore. Said out
   *  loud either way — a copy that silently did nothing is the one failure
   *  that matters, which is the rule `lib/clipboard` exists for. */
  const copyAnswer = async (text: string) => {
    setCopied((await copyToClipboard(text)) ? "done" : "failed");
  };

  /** A citation goes straight to the record — chip and source row alike. The
   *  shell's own openers, so the workspace and every other URL parameter
   *  survive the jump. */
  const openCitation = (target: CitationTarget) => {
    if (target.target === "item") onOpenItem(target.id);
    else onOpenWalk(target.id);
  };

  /** The console URL a citation's destination resolves to — the shell's own
   *  `hrefForItem`/`hrefForWalk`, so a chip or a source row can carry a real
   *  `href` (middle-click, cmd-click, hover preview) and still open through
   *  `openCitation` above, the same convention every other record row in the
   *  console follows. */
  const hrefForCitation = (target: CitationTarget): string =>
    target.target === "item" ? hrefForItem(target.id) : hrefForWalk(target.id);

  return (
    <div
      data-testid="ask-workspace"
      // Same bounded, two-column work surface as Review Queue. Keeping the
      // overflow on this frame (rather than the document) lets both the
      // conversation and its history list scroll independently.
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        background: C.pageGround,
        overflow: "hidden",
      }}
    >
      {/* Recent chats remain anchored to console-main's left edge. */}
      <div style={compact ? {
        position: "absolute", inset: "0 auto 0 0", zIndex: 6,
        display: "flex", background: C.pageGround,
      } : { display: "contents" }}>
        <AskHistoryRail
          entries={history}
          loading={historyLoading}
          selectedId={openEntryId}
          open={railOpen}
          onToggle={() => compact
            ? setCompactHistoryOpen((open) => !open)
            : setHistoryOpen((open) => !open)}
          onSelect={openExchange}
          onNew={reset}
          problem={historyProblem}
          now={railNow}
        />
      </div>

      <div
        data-testid="ask-conversation-pane"
        style={{
          position: "relative",
          marginLeft: compact ? ASK_RAIL_COLLAPSED_WIDTH : 0,
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: C.pageGround,
          overflow: "hidden",
        }}
      >
          {/*
          One scrollport for the whole conversation, and the reading measure
          inside it.

          The design draws no page header here — no crumb, no "Ask Led AI"
          title. On the opening screen the question IS the title, centred in
          the window; in a thread the heading is a mono line beside "New ask".
          So this view does not use `ViewHeader`/`ViewBody` the way the rest
          of the console does, and the 48px gutter and 800px measure are the
          design's own.
        */}
        <div
          data-testid="ask-scroll"
          ref={scrollRef}
          onScroll={onScroll}
          className="sb"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowAnchor: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // The rail stays fixed at console-main's left edge. Reserving its
            // width on the right centres the prose in console-main itself,
            // rather than in the leftover space after Recent chats.
            padding: compact ? "0 16px" : `0 ${(historyOpen ? ASK_RAIL_WIDTH : ASK_RAIL_COLLAPSED_WIDTH) + 48}px 0 48px`,
            boxSizing: "border-box",
          }}
        >
          {conversationNotice && (
            <div
              role="status"
              data-testid="ask-conversation-notice"
              style={{
                marginTop: 20, padding: "12px 14px", borderRadius: 12,
                background: C.surface, border: `1px solid ${C.line}`,
                color: C.sub, fontSize: 15.52, lineHeight: 1.5,
                maxWidth: ASK_MEASURE,
              }}
            >
              {conversationNotice}
            </div>
          )}
          {asked ? (
            <div
              data-testid="ask-thread"
              ref={threadRef}
              style={{
                width: "100%",
                maxWidth: ASK_MEASURE,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 26,
                padding: "28px 0 24px",
                paddingBottom: "calc(var(--ask-composer-height, 128px) + 40px)",
                boxSizing: "border-box",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* The way out of an exchange, at the TOP where the design
                    puts it. It used to be a "Start over" line under the
                    answer, which is the one place a reader who has finished
                    reading is no longer looking. */}
                <button
                  type="button"
                  className="kb"
                  data-testid="ask-reset"
                  onClick={reset}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    height: 30,
                    padding: "0 10px 0 8px",
                    borderRadius: 8,
                    border: "none",
                    background: "transparent",
                    font: "inherit",
                    fontSize: 14.95,
                    fontWeight: 500,
                    color: C.sub,
                    cursor: "pointer",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M12 5v14M5 12h14"
                      stroke={C.sub2}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  New ask
                </button>
                <span style={{ flex: 1 }} />
                {/* The design puts the scope here ("66 Pearl · this week").
                    This console has no time scope on Ask and no project name
                    to hand in this view, so the slot says the one thing that
                    IS true of every answer and matters most — nothing here
                    writes. */}
                <span style={{ ...META, fontSize: 10.93, color: C.sub2 }}>Read only</span>
              </div>

              {/* Pushes a short exchange to the FOOT of the page, against the
                  composer, rather than stranding two lines at the top of an
                  empty screen. The design's own spacer. */}
              <div style={{ flex: 1 }} />

              {priorTurns.map((turn) => (
                <PriorAskTurnView
                  search={search}
                  key={turn.id}
                  turn={turn}
                  onOpen={openCitation}
                  hrefFor={hrefForCitation}
                />
              ))}

              <div
                data-testid="ask-question"
                className="ask-sent"
                style={{
                  alignSelf: "flex-end",
                  maxWidth: "72%",
                  padding: "12px 16px",
                  // The design tints the reader's own words and leaves the
                  // answer bare. A bordered white bubble read as a card —
                  // one more surface on a page that is mostly surfaces.
                  borderRadius: "18px 18px 6px 18px",
                  background: C.askSaid,
                  color: C.ink,
                  fontSize: 17.25,
                  lineHeight: 1.5,
                }}
              >
                {asked}
              </div>

              {/* TOP-aligned, not centred. The row used to stretch and the
                  mark centred itself over the whole answer — text, the
                  records line AND the sources card — so on a four-line
                  answer it sat level with "311 records read" instead of
                  beside the sentence it marks (user, 2026-09-10). */}
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: C.ink,
                    opacity: 0.9,
                    // Exactly one line of the answer tall, so the mark is
                    // OPTICALLY centred on the first line rather than hung
                    // from its top — and stays there if the answer's scale
                    // changes, because both numbers come from `AnswerBody`.
                    height: ANSWER_FONT_SIZE * ANSWER_LINE_HEIGHT,
                  }}
                >
                  {/* The BADGE, not the full lockup: a third wordmark in this
                      space would say the product's name three times to answer
                      one question (user-directed, 2026-09-09). `Lockup` is
                      the one place the artwork lives. */}
                  <Lockup variant="mark" height={22} />
                </span>

                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                  {asking && (
                    <div
                      data-testid="ask-pending"
                      style={{
                        ...META,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 10.93,
                        color: C.sub2,
                      }}
                    >
                      Reading your walks
                      {/* The wait is the part that used to look like a stall.
                          The stagger is an inline delay on an animation that
                          only exists when motion is welcome — with it off the
                          dots are three still marks, and the words are the
                          same. */}
                      <span style={{ display: "flex", gap: 3 }} aria-hidden>
                        {[0, 1, 2].map((index) => (
                          <span
                            key={index}
                            data-testid="ask-dot"
                            className="ask-dot"
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: 2,
                              background: C.sub2,
                              animationDelay: `${index * 0.18}s`,
                            }}
                          />
                        ))}
                      </span>
                    </div>
                  )}

                  {problem && (
                    <div
                      data-testid="ask-error"
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        background: C.redSoft,
                        fontSize: 15.52,
                        lineHeight: 1.5,
                      }}
                    >
                      {problem}
                    </div>
                  )}

                  {answer && (
                    <>
                      <AnswerBody
                        text={answer.answer}
                        references={answer.references}
                        onOpen={openCitation}
                        hrefFor={hrefForCitation}
                      />
                      <SourcesCard
                        text={answer.answer}
                        references={answer.references}
                        onOpen={openCitation}
                        hrefFor={hrefForCitation}
                      />
                      <AskMatchingRecords resultSets={answer.resultSets} search={search} onOpen={openCitation} hrefFor={hrefForCitation} />
                      {answer.countedRecordCount === undefined && !answer.resultSets?.length && citedInOrder(answer.answer, answer.references).length === 0 && (
                        // An answer with nothing behind it is worth saying
                        // out loud: this console's whole argument is "the
                        // photo and the quote that prove it".
                        <div
                          data-testid="ask-uncited"
                          style={{ fontSize: 14.37, color: C.sub2 }}
                        >
                          Ask Led AI cited no specific record for this answer.
                        </div>
                      )}

                      {/*
                        The design's footer row under an answer holds two
                        action pills ("Nudge masonry", "Assign the punch
                        item") and three icon buttons (copy, good answer,
                        wrong). Only Copy is here.

                        The pills act on the answer's own subject, which
                        means writing — and this tab is read-only by
                        construction; the footnote on the opening screen says
                        so. The two feedback buttons have nowhere to send a
                        verdict: there is no route that records one, so a tick
                        that vanished on the next question would be a control
                        that consumed the reader's judgement and dropped it.
                      */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AnswerDetails answer={answer} />
                        <span style={{ flex: 1 }} />
                        <button
                          type="button"
                          className="kb"
                          data-testid="ask-copy"
                          aria-label="Copy this answer"
                          title={copied === "done" ? "Copied" : copied === "failed" ? "Could not copy" : "Copy this answer"}
                          onClick={() => void copyAnswer(answer.answer)}
                          style={{
                            display: "grid",
                            placeItems: "center",
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            border: "none",
                            background: "transparent",
                            color: copied === "failed" ? C.red : copied === "done" ? C.green : C.sub2,
                            cursor: "pointer",
                          }}
                        >
                          {copied === "done" ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M4 12.5l5 5L20 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                              <path
                                d="M15 5.5A2.5 2.5 0 0 0 12.5 4h-6A2.5 2.5 0 0 0 4 6.5v6A2.5 2.5 0 0 0 5.5 15"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              data-testid="ask-hero"
              style={{
                width: "100%",
                maxWidth: ASK_MEASURE,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                // The design CENTRES the opening screen in the window rather
                // than hanging it from the top. That is what the page wanted
                // when the title read "a bit too high" (user, 2026-09-11) —
                // a fixed drop was the guess, this is the answer, and it
                // holds at every window height instead of one.
                justifyContent: "center",
                gap: 28,
                padding: "48px 0 32px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 14,
                  textAlign: "center",
                }}
              >
                <span style={{ display: "inline-flex", opacity: 0.9 }}>
                  <Lockup variant="mark" height={34} />
                </span>
                <h1
                  data-testid="ask-hero-title"
                  style={{
                    margin: 0,
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: "-0.7px",
                    lineHeight: 1.15,
                    textWrap: "balance",
                  }}
                >
                  What do you want to know about the field?
                </h1>
                <div
                  style={{
                    fontSize: 16.1,
                    color: C.sub,
                    maxWidth: 440,
                    lineHeight: 1.5,
                    textWrap: "pretty",
                  }}
                >
                  Led answers from your walks, records and photos — and tells you when it
                  doesn’t know.
                </div>
              </div>

              <AskComposerCard
                search={search}
                mentionPlacement="below"
                value={question}
                onValue={setQuestion}
                onSubmit={() => void ask(question)}
                busy={asking || restoring}
                unavailable={backendMissing}
                placeholder="Ask anything about the field…"
              />

              {problem && (
                <div
                  data-testid="ask-error"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: C.redSoft,
                    fontSize: 15.52,
                    lineHeight: 1.5,
                  }}
                >
                  {problem}
                </div>
              )}

              {/* Cards in a grid, not a stack of full-width rows. Four
                  questions read as four choices this way; stacked, they read
                  as a list the reader is meant to work through. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,300px),1fr))",
                  gap: 10,
                }}
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.q}
                    type="button"
                    className="kb askcard"
                    data-testid="ask-suggestion"
                    onClick={() => ask(suggestion.q)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      padding: "16px 16px 14px",
                      borderRadius: 14,
                      background: C.surface,
                      border: `1px solid ${C.askCardEdge}`,
                      minWidth: 0,
                      textAlign: "left",
                      font: "inherit",
                      cursor: "pointer",
                    }}
                  >
                    {/*
                      The design heads each card with a mono eyebrow and an
                      icon — PAST DUE, TODAY, MANPOWER, PHOTOS. There is none
                      here because there is nothing to put in it: these
                      prompts come from `GET /api/ask/suggestions` as bare
                      strings, and a category inferred from the words would
                      be this console labelling a question with a guess. The
                      four the design draws are its own copy, not a field the
                      route returns.
                    */}
                    <span
                      style={{
                        fontSize: 16.67,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: C.ink,
                        textWrap: "pretty",
                      }}
                    >
                      {suggestion.q}
                    </span>
                    {suggestion.note && (
                      <span style={{ fontSize: 13.8, color: C.sub, lineHeight: 1.45 }}>
                        {suggestion.note}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div style={{ fontSize: 13.8, color: C.sub2, textAlign: "center" }}>
                Nothing you ask here changes a record until you say so.
              </div>
            </div>
          )}
      </div>
      {asked && (
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: compact ? 16 : 48,
            right: compact ? 16 : (historyOpen ? ASK_RAIL_WIDTH : ASK_RAIL_COLLAPSED_WIDTH) + 48,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div ref={composerRef} data-testid="ask-follow-up" style={{ width: "100%", maxWidth: ASK_MEASURE, pointerEvents: "auto" }}>
            <AskComposerCard
              search={search}
              value={question}
              onValue={setQuestion}
              onSubmit={() => void ask(question)}
              busy={asking || restoring}
              unavailable={backendMissing}
              placeholder="Follow up…"
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

/**
 * The composer, as the design's CARD.
 *
 * The console's other chat box is a 62px pill (`CHAT_INPUT_STYLE`, shared
 * with the Brief). Ask's is a taller card: the question above, its controls
 * on a row beneath. That is not decoration — it is what lets a long question
 * wrap instead of scrolling sideways inside a single line, which is the
 * failure the pill has and the reason the design changed it here.
 *
 * ## What the design's toolbar has that this one does not
 *
 * A project pill, a "This week" pill and a camera. All three are cut, for
 * one reason each and the same reason overall — a control that appears live
 * and quietly does nothing spends the reader's trust once:
 *
 *  - the **project** and **date** pills scope the question. The console has
 *    no time scope on Ask at all, and the project scope it does have already
 *    lives in the sidebar, where it applies to every page rather than to
 *    this one box. Two scope controls that could disagree is worse than one.
 *  - the **camera** attaches a photo to the question. `askAttachment.ts` can
 *    read one and `/api/ask` accepts one, but only Home currently hands one
 *    over; wiring a picker here is a feature, not a look, and shipping the
 *    icon without it would be a button that opens nothing.
 */
function AskComposerCard({
  search,
  mentionPlacement = "above",
  value,
  onValue,
  onSubmit,
  busy,
  unavailable,
  placeholder,
}: {
  search: string;
  mentionPlacement?: "above" | "below";
  value: string;
  onValue: (next: string) => void;
  onSubmit: () => void;
  busy: boolean;
  /** No backend to reach. The box says so and refuses input. */
  unavailable: boolean;
  placeholder: string;
}) {
  const disabled = busy || unavailable;
  return (
    <div
      data-testid="ask-composer-card"
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 20,
        background: C.surface,
        border: `1px solid ${C.popLine}`,
        boxShadow: `0 1px 2px ${C.softShadow}, 0 10px 30px ${C.askLift}`,
        opacity: unavailable ? 0.6 : 1,
      }}
    >
      <AskMentionInput
        search={search}
        placement={mentionPlacement}
        onValue={onValue}
        data-testid="ask-input"
        className="askc-input"
        aria-label="Ask Led AI a question"
        value={value}
        disabled={disabled}
        data-readonly={unavailable ? "true" : "false"}
        // `onInput`, not `onChange`. They are the same event for a text input
        // in React, and this one is the one that actually fires under the
        // console's own test DOM (React 19 + happy-dom never synthesises the
        // change event for a text input, so an `onChange` handler here would
        // be untestable — and an untestable composer is how a typed question
        // silently stops reaching the send button).
        onInput={(event) => onValue(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          onSubmit();
        }}
        placeholder={unavailable ? "Ask Led AI is unavailable on this console" : placeholder}
        style={{
          border: 0,
          outline: "none",
          background: "transparent",
          padding: "16px 20px 6px",
          minHeight: 24,
          font: "inherit",
          fontSize: 18.4,
          lineHeight: 1.5,
          color: C.ink,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px 8px 10px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ flex: 1 }} />
        <DictationButton
          disabled={disabled}
          onTranscript={(text) =>
            onValue(`${value}${value && !/\s$/.test(value) ? " " : ""}${text}`)
          }
        />
        <button
          data-testid="ask-send"
          type="button"
          className="kb"
          aria-label="Ask"
          disabled={disabled}
          onClick={onSubmit}
          style={{
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            width: 36,
            height: 36,
            marginLeft: 2,
            padding: 0,
            borderRadius: 999,
            border: "none",
            background: C.ink,
            color: C.surface,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <ChatSendIcon />
        </button>
      </div>
    </div>
  );
}
